import { createContext, useContext, useState, useEffect, useRef } from 'react';

const ScrollContext = createContext();

export const ScrollProvider = ({ children }) => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('down');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('portal'); // Changed from 'home'
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollVelocity, setScrollVelocity] = useState(0);
  
  const lastScrollY = useRef(0);
  const lastScrollTime = useRef(Date.now());
  const scrollTimeout = useRef(null);
  const rafId = useRef(null);

  // Track scroll position with requestAnimationFrame for performance
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        rafId.current = requestAnimationFrame(() => {
          updateScrollPosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    const updateScrollPosition = () => {
      const currentScrollY = window.scrollY;
      const currentTime = Date.now();
      const timeDelta = currentTime - lastScrollTime.current;
      
      // Calculate velocity (pixels per second)
      const velocity = Math.abs(currentScrollY - lastScrollY.current) / (timeDelta / 1000);
      setScrollVelocity(velocity);
      
      // Update scroll position
      setScrollY(currentScrollY);
      
      // Determine direction
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }
      
      // Calculate scroll progress (0 to 1)
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? currentScrollY / docHeight : 0;
      setScrollProgress(progress);
      
      // Update last values
      lastScrollY.current = currentScrollY;
      lastScrollTime.current = currentTime;
      
      // Set scrolling state
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Set timeout to mark scrolling as stopped
      scrollTimeout.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  // Detect active section based on scroll position (debounced)
  useEffect(() => {
    const updateActiveSection = () => {
      // Updated to match your actual slide IDs
      const sections = [
        { id: 'portal', selector: '#portal' },
        { id: 'equation', selector: '#equation' },
        { id: 'leadership', selector: '#leadership' },
        { id: 'services', selector: '#services' },
        { id: 'team', selector: '#team' },
        { id: 'legacy', selector: '#legacy' },
        { id: 'recruitment', selector: '#recruitment' },
        { id: 'interface', selector: '#interface' }
      ];

      const viewportHeight = window.innerHeight;
      const viewportMiddle = scrollY + (viewportHeight / 2);

      for (const section of sections) {
        const element = document.querySelector(section.selector);
        if (element) {
          const { top, bottom } = element.getBoundingClientRect();
          const absoluteTop = top + scrollY;
          const absoluteBottom = bottom + scrollY;

          if (viewportMiddle >= absoluteTop && viewportMiddle <= absoluteBottom) {
            if (activeSection !== section.id) {
              setActiveSection(section.id);
            }
            break;
          }
        }
      }
    };

    // Only update active section when scrolling stops or every 500ms
    const timeoutId = setTimeout(updateActiveSection, isScrolling ? 100 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [scrollY, isScrolling, activeSection]);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(`#${sectionId}`);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setActiveSection(sectionId);
    }
  };

  const scrollToTop = (smooth = true) => {
    window.scrollTo({
      top: 0,
      behavior: smooth ? 'smooth' : 'auto'
    });
    setActiveSection('portal'); // Changed from 'home'
  };

  const scrollToBottom = (smooth = true) => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: smooth ? 'smooth' : 'auto'
    });
  };

  const isElementInViewport = (element, threshold = 0.1) => {
    if (!element) return false;
    
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const thresholdPx = viewportHeight * threshold;
    
    return (
      rect.top <= viewportHeight - thresholdPx &&
      rect.bottom >= thresholdPx
    );
  };

  const value = {
    scrollY,
    scrollDirection,
    scrollProgress,
    activeSection,
    isScrolling,
    scrollVelocity,
    scrollToSection,
    scrollToTop,
    scrollToBottom,
    isElementInViewport
  };

  return (
    <ScrollContext.Provider value={value}>
      {children}
    </ScrollContext.Provider>
  );
};

export const useScroll = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScroll must be used within a ScrollProvider');
  }
  return context;
};